<template>
  <div class="countdown">
    <div :class="countdownDescriptionContainerClasses">
      <UCIcon icon="fa-alarm-clock" size="fa-lg" inverse class="countdown__timer-icon" />
      <p class="countdown__description">
        {{ timerDescription }}
      </p>
    </div>
    <div class="countdown__units">
      <div
        v-for="([unit, unitValue], index) in Object.entries(timer)"
        :key="index"
        :class="countdownUnitClasses"
      >
        <div class="countdown__value-outer">
          <div class="countdown__value-inner">
            {{ useZeroPadding ? unitValue.toString().padStart(2, '0') : unitValue }}
          </div>
        </div>
        <div class="countdown__label">
          {{ unit }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from '@nuxtjs/composition-api';
import UCIcon from '@shared/components/@atoms/UCIcon/UCIcon.vue';
import responsiveMixin from '@shared/ui/utilities/mixins/responsiveMixin';
import dayjs from 'dayjs';

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_MIN = 1000 * 60;
const MS_PER_SEC = 1000;

export default defineComponent({
  name: 'UCCountdown',
  components: {
    UCIcon,
  },
  mixins: [responsiveMixin],
  props: {
    countdownTime: {
      type: String,
      required: true,
    },
    useZeroPadding: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      timer: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      },
      showDealBanner: true,
      dealTimer: 0,
      isExpired: false,
    };
  },
  computed: {
    timerDescription() {
      return this.isExpired ? 'Deal Has Expired' : 'Deal Expires In';
    },
    countdownDescriptionContainerClasses() {
      return [
        'countdown__description-container',
        { 'countdown__description-container--expired': this.isExpired },
      ];
    },
    countdownUnitClasses() {
      return ['countdown__unit', { 'countdown__unit--expired': this.isExpired }];
    },
  },
  mounted() {
    this.countDown();
  },
  beforeDestroy() {
    window.clearInterval(this.dealTimer);
  },
  methods: {
    countDown() {
      this.dealTimer = window.setInterval(() => {
        const now = new Date().getTime();
        const timeLeft = dayjs(this.countdownTime).toDate().getTime() - now;
        if (timeLeft < 0) {
          this.isExpired = true;
          this.showDealBanner = false;
          window.clearInterval(this.dealTimer);
          this.timer = { days: 0, hours: 0, minutes: 0, seconds: 0 };
        } else {
          this.isExpired = false;
          this.timer = {
            days: Math.floor(timeLeft / MS_PER_DAY),
            hours: Math.floor((timeLeft % MS_PER_DAY) / MS_PER_HOUR),
            minutes: Math.floor((timeLeft % MS_PER_HOUR) / MS_PER_MIN),
            seconds: Math.floor((timeLeft % MS_PER_MIN) / MS_PER_SEC),
          };
        }
      }, 1000);
    },
  },
});
</script>

<style lang="scss" scoped>
.countdown {
  display: var(--countdown-display);
  flex-direction: var(--countdown-flex-direction);
  align-items: var(--countdown-align-items);
  width: var(--countdown-width, auto);
  max-width: 100%;
  padding: var(--countdown-padding, 0);
  margin-top: 0;
  border-radius: var(--countdown-border-radius, 0);
  background: var(--countdown-background, transparent);

  &__timer-icon {
    margin-right: 1rem;
  }

  &__description {
    font-family: var(--countdown-description-font-family, var(--font-family--primary)), sans-serif;
    font-size: var(--countdown-description-font-size, var(--font-size--lg));
    font-weight: var(--countdown-description-font-weight, var(--font-weight--bold));
    text-transform: uppercase;
    margin: var(--countdown-description-margin, 0);
    margin-right: 1.25rem;
  }

  &__description-container {
    display: flex;
    align-items: center;

    // active class
    .countdown__timer-icon {
      path {
        stroke: var(--countdown-icon-color, var(--c-dark));
      }
    }

    .countdown__description {
      color: var(--countdown-description-color, var(--c-dark));
    }

    // expired class
    &--expired {
      .countdown__timer-icon {
        path {
          stroke: var(--countdown-icon-color-expired, var(--c-gray-variant));
        }
      }

      .countdown__description {
        color: var(--countdown-description-color-expired, var(--c-gray-variant));
      }
    }
  }

  &__units {
    display: flex;
    align-items: center;
  }

  @include for-mobile {
    &__timer-icon {
      margin-right: 1rem;
    }

    &__description {
      margin-right: 1rem;
    }

    &__description-container {
      display: flex;
      align-items: center;
    }
  }

  /*
   * NB __value-outer and __value_inner are a necessary construct to support border with a grandient and border radius
   * See https://css-tricks.com/gradient-borders-in-css/
   */
  &__value-outer {
    border-radius: var(--countdown-value-border-radius, 0);
  }

  &__value-inner {
    font-family: var(--countdown-value-font-family, var(--font-family--primary));
    font-size: var(--countdown-value-font-size);
    font-weight: var(--countdown-value-font-weight);
    text-align: center;
    padding: var(--countdown-value-padding, 0);
    border-radius: var(--countdown-value-inner-border-radius);
    background: var(--countdown-value-background, var(--c-light-variant));
  }

  &__label {
    color: var(--countdown-label-color, var(--c-dark));
    font-family: var(--countdown-label-font-family, var(--font-family--primary));
    font-size: var(--countdown-label-font-size);
    font-weight: var(--countdown-label-font-weight);
    text-transform: uppercase;
    text-align: center;
    padding: var(--countdown-label-padding, 0);
    margin: var(--countdown-label-margin, 0);
    background: var(--countdown-label-background, transparent);
  }

  &__unit {
    display: flex;
    justify-content: center;
    flex-direction: column;
    width: var(--countdown-unit-width, auto);
    height: var(--countdown-unit-height, auto);
    margin: var(--countdown-unit-margin, 0);

    &:first-of-type {
      margin: var(--countdown-unit-margin-first, 0);
    }

    // active class
    .countdown__value-outer {
      padding: var(--countdown-value-border-width, 2px);
      background: var(--countdown-value-border-background);
    }

    .countdown__value-inner {
      color: var(--countdown-value-color, var(--c-dark));
    }

    .countdown__label {
      color: var(--countdown-value-color, var(--c-dark));
    }

    // expired class
    &--expired {
      .countdown__value-outer {
        border: var(--countdown-value-outer-expired, 2px solid var(--c-text-disabled));
        background: none;
      }

      .countdown__value-inner {
        color: var(--countdown-value-color-expired, var(--c-gray-variant));
      }

      .countdown__label {
        color: var(--countdown-value-color-expired, var(--c-gray-variant));
      }
    }
  }
}
</style>
